"use client";

import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Button, Card, Flex, Heading, Icon } from "@highereducation/hen-ui";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import { CompactProductListItem } from "./components/CompactProductListItem";
import type { CompactProductListProps } from "./types";
import { hyphenateForTagular } from "@/utils/tagular/helpers";
import useElementEvents from "@/hooks/eventing/useElementEvents";

export function CompactProductList({
  title,
  ctaUrl,
  ctaLabel,
  ctaHtmlId,
  products,
  location,
}: CompactProductListProps) {
  const { ref: ctaRef, inView: ctaInView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );
  // extra guard so view event doesn't trigger again on expand toggle
  const [ctaAlreadyViewed, setCtaAlreadyViewed] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(-1);

  const ctaWebElement = useMemo(
    () => ({
      elementType: "BUTTON",
      htmlId: ctaHtmlId
        ? `${ctaHtmlId}-${hyphenateForTagular(title)}`
        : undefined,
      location,
      name: hyphenateForTagular(ctaLabel),
      text: ctaLabel,
    }),
    [ctaHtmlId, ctaLabel, location, title],
  );

  const { elementClicked: ctaClicked, elementViewed: ctaViewed } =
    useElementEvents({ webElement: ctaWebElement });

  const handleExpandToggle = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? -1 : index));
  };

  const handleCtaClick = () => {
    ctaClicked();
  };

  useEffect(() => {
    if (ctaInView && !ctaAlreadyViewed) {
      ctaViewed();
      setCtaAlreadyViewed(true);
    }
  }, [ctaInView, ctaViewed, ctaAlreadyViewed]);

  return (
    <Card
      data-hen-ui
      variant="background-transparent"
      className="mx-auto box-border w-full h-full rounded-[1.25rem] border-0 bg-fern-100 py-5 px-4"
    >
      <Flex
        direction="column"
        align="center"
        justify="between"
        gap="lg"
        className="w-full h-full"
      >
        <Flex direction="column" align="center" gap="lg" className="w-full">
          <Heading variant="h4" className="m-0 w-full">
            {title}
          </Heading>

          <Flex
            direction="column"
            align="stretch"
            gap="xs"
            className="w-full pb-1"
          >
            {products.map((product, index) => (
              <CompactProductListItem
                key={product.productUuid}
                product={product}
                sectionTitle={title}
                location={location}
                index={index}
                isExpanded={expandedIndex === index}
                onExpandToggle={() => handleExpandToggle(index)}
              />
            ))}
          </Flex>
        </Flex>

        <Button
          ref={ctaRef}
          onClick={handleCtaClick}
          variant="textOnly"
          className="gap-2 rounded-md p-0 font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 focus-visible:ring-offset-fern-100"
          asChild
        >
          <a href={ctaUrl}>
            <Icon icon="search" className="h-4 w-4" aria-hidden />
            {ctaLabel}
          </a>
        </Button>
      </Flex>
    </Card>
  );
}

export { CompactProductListSearchWrapper } from "./CompactProductListSearchWrapper";
export type {
  CompactProductListSearchWrapperProps,
  CompactProductListProps,
} from "./types";
